import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Clock, Flame } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { grammarCourseFor } from '@/apps';
import { STAR_LABELS } from '@/apps/shared/grammar/score';
import { StarRating } from '@/apps/shared/grammar/StarRating';
import { readingOrder } from '@/apps/shared/grammar/types';
import { MissedList } from '@/components/practice/MissedList';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import type { SessionFinishResponse, SessionMiss, SessionMode } from '@/core/types';
import { api } from '@/lib/api';
import { useAuxLanguages } from '@/lib/queries';

type LocationState = {
  result: SessionFinishResponse;
  mode: SessionMode;
  /** Wrong answers collected by the practice shell. Absent on a clean session. */
  misses?: SessionMiss[];
  /**
   * The grammar lesson this session actually taught, if any.
   *
   * Null on every non-grammar app, and null on a post-curriculum review — where
   * the lesson on screen is "the rule you are weakest on" rather than one just
   * studied, so asking how well it landed would collect an opinion about
   * something the session never taught.
   */
  ratedLesson?: { n: number; title: Record<string, string> } | null;
  /** The query string the finished session was started with. */
  search?: string;
} | null;

export function ResultsRoute() {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  if (!state) {
    // Direct navigation to /results without state — bounce home.
    return (
      <div className="flex min-h-screen items-center justify-center text-center">
        <div>
          <p className="text-muted-foreground">No session results to show.</p>
          <Button className="mt-4" onClick={() => navigate('/')}>
            Return home
          </Button>
        </div>
      </div>
    );
  }

  const { result, mode, misses = [], ratedLesson = null, search } = state;
  const accuracyPct = Math.round(result.accuracy * 100);
  const message = motivationalMessage(result.cards_correct, result.cards_total);

  return (
    <div className="bg-background flex min-h-screen flex-col">
      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-4 py-10 sm:px-6">
        <div className="my-auto flex w-full flex-col gap-6">
          <header className="text-center">
            <h1 className="font-display text-3xl font-semibold">Session complete</h1>
          </header>

          <section className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="font-display text-6xl font-bold tabular-nums">
              {result.cards_correct} / {result.cards_total}
            </div>
            <div className="text-muted-foreground text-lg">{accuracyPct}% accuracy</div>
            <div className="font-display mt-2 text-xl">{message}</div>
          </section>

          {result.streak_eligible && (
            <>
              <Separator />
              <section className="flex flex-col items-center gap-3 py-2 text-center">
                <h2 className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                  Today's streak status
                </h2>
                <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
                  <span className="inline-flex items-center gap-1.5 text-base font-medium">
                    <Flame className="text-streak size-5" />
                    {result.streak.current_streak}-day streak
                  </span>
                  {result.apps_done_today.map((app) => (
                    <span
                      key={app.id}
                      className="text-success inline-flex items-center gap-1 text-sm"
                    >
                      <Check className="size-4" />
                      {app.name} done
                    </span>
                  ))}
                  {result.apps_remaining_today.map((app) => (
                    <span
                      key={app.id}
                      className="text-muted-foreground inline-flex items-center gap-1 text-sm"
                    >
                      <Clock className="size-4" />
                      {app.name} still needed
                    </span>
                  ))}
                </div>
              </section>
            </>
          )}

          {ratedLesson && (
            <>
              <Separator />
              <RatingPrompt
                appId={appId!}
                sessionId={result.session_id}
                lessonN={ratedLesson.n}
                title={ratedLesson.title}
              />
            </>
          )}

          {misses.length > 0 && (
            <>
              <Separator />
              <MissedList appId={appId!} misses={misses} />
            </>
          )}

          {/* Pinned so a long miss list never buries the actions. The border is
              the bar's own, not a scrolling Separator: content passes under it,
              and without an edge the list looks clipped rather than scrolled. */}
          <section className="bg-background border-border sticky bottom-0 flex flex-col gap-3 border-t py-4 sm:flex-row sm:justify-center">
            <Button
              size="lg"
              className="flex-1 sm:flex-none sm:px-8"
              // Repeat exactly what was just done, filters and all. Falls back
              // to the bare mode for a session started before this shipped.
              onClick={() =>
                navigate(`/apps/${appId}/practice?${search || `mode=${mode}`}`, { replace: true })
              }
            >
              Practice again
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="flex-1 sm:flex-none sm:px-8"
              onClick={() => navigate('/')}
            >
              Return home
            </Button>
          </section>
        </div>
      </main>
    </div>
  );
}

/**
 * "How well did that land?" — asked once, answered in one tap, skippable.
 *
 * The score above it says whether the answers were right, which is a different
 * question. A lesson can be answered correctly by pattern-matching four
 * gap-fills and still leave the rule unusable in a sentence you have not seen;
 * another reads 58% because the exercises are fiddly while the rule is clear.
 * Only the person who just did it knows which, so this is the one number the
 * app does not compute.
 *
 * It saves on tap and stays on screen afterwards, so a mis-tap is corrected by
 * tapping the right star — the day's rating is replaced rather than appended
 * to, and a slip never becomes a permanent entry in the history.
 *
 * Nothing blocks the buttons below it. A rating that must be given to leave the
 * screen is one that gets tapped at random within a week, and random taps are
 * worse than no data because they look like data.
 */
function RatingPrompt({
  appId,
  sessionId,
  lessonN,
  title,
}: {
  appId: string;
  sessionId: number;
  lessonN: number;
  title: Record<string, string>;
}) {
  const queryClient = useQueryClient();
  const { primaryLang } = useAuxLanguages();
  const binding = grammarCourseFor(appId);
  const [stars, setStars] = useState<number | null>(null);

  const name = binding ? readingOrder(title, binding.course, primaryLang)[0] : undefined;

  const rate = useMutation({
    mutationFn: (value: number) => api.saveGrammarRating(appId, lessonN, value, sessionId),
    onSuccess: (_data, value) => {
      setStars(value);
      queryClient.invalidateQueries({ queryKey: [appId, 'ratings'] });
    },
    onError: (err: Error) => toast.error('Could not save that', { description: err.message }),
  });

  return (
    <section className="flex flex-col items-center gap-2 py-4 text-center">
      <h2 className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
        How well did lesson {lessonN} land?
      </h2>
      {name && <p className="font-display text-lg">{name}</p>}
      <StarRating
        value={stars}
        onRate={(value) => rate.mutate(value)}
        disabled={rate.isPending}
        size="lg"
        label={name ?? `lesson ${lessonN}`}
      />
      <p className="text-muted-foreground min-h-5 text-sm">
        {stars === null ? 'Optional — it only feeds your own review list.' : STAR_LABELS[stars]}
      </p>
    </section>
  );
}

/**
 * The line under the score.
 *
 * Proportional, not an absolute miss count. It used to read `wrong <= 2` →
 * "Excellent!", which was tuned for the 15-card sessions every app had at the
 * time: two misses out of fifteen genuinely is excellent. Konjugation's default
 * session is five cards, where the same rule calls 3/5 — 60% — excellent, and
 * praise that arrives regardless of the score is worth nothing.
 *
 * Thresholds are on accuracy so the message means the same thing whatever the
 * session length, and the floor keeps a one-miss short session from being
 * scored harshly: 4/5 is 80%, which should not read as "Good effort".
 */
function motivationalMessage(correct: number, total: number): string {
  if (total === 0) return 'Session complete';
  if (correct === total) return 'Perfect!';
  const wrong = total - correct;
  const accuracy = correct / total;
  // One miss is one miss, however short the session.
  if (wrong === 1 || accuracy >= 0.9) return 'Excellent!';
  if (accuracy >= 0.75) return 'Great job!';
  if (accuracy >= 0.5) return 'Good effort!';
  return 'Keep practicing!';
}
